/**
 * shiftDataService — vardiya + giriş/çıkış (attendance) icin SQL-once + Firebase okuma-yedekli adaptor.
 *
 * Backend: gts_hr_api (`/api/hr/shifts`, `/api/hr/attendance`) — `hrApi` client (X-Company-Id otomatik).
 * OKUMA: once SQL; bos/hata + HybridFallback ise Firebase `companies/{cid}/shifts` (+ `vardiyaListesi`)
 *        okunur ve SQL'e tohumlanir (lazy backfill: `/api/hr/shifts/sync`, `/api/hr/attendance/sync`).
 *        SqlOnly ise Firebase'e BAKILMAZ.
 * YAZMA:  create/update/delete/assign + attendance handle/edit DAIMA dogrudan SQL'e gider (Firebase'e yazmaz).
 *
 * NOT: SQL'de vardiya kimligi integer Id'dir; RTDB fallback'inde push key (string) doner.
 *      RTDB push key SQL tarafinda Shift.legacyFirebaseKey'e karsilik gelir.
 */

import { hrApi } from '../../config/apiClient';
import { getMigrationMode } from './migrationMode';

export interface Shift {
  /** SQL integer Id (SQL modu) veya RTDB push key (fallback). */
  id: number | string;
  name: string;
  startTime: string;
  endTime: string;
  lateTolerance: number;
  earlyExitTolerance: number;
  isNextDayExit: boolean;
  branchesId?: string | null;
  /** "single" (tek QR/otomatik) | "dual" (çift QR/çoklu oturum). */
  attendanceMode?: string;
  legacyFirebaseKey?: string | null;
  isActive?: boolean;
  assignedCount?: number;
  /** RTDB fallback'inde inline gelir; SQL modunda getShiftPersonnel ile alinir. */
  personnelUids?: string[];
  [key: string]: any;
}

export interface ShiftWriteInput {
  name: string;
  startTime: string;
  endTime: string;
  lateTolerance?: number;
  earlyExitTolerance?: number;
  isNextDayExit?: boolean;
  branchesId?: string | null;
  /** "single" | "dual". */
  attendanceMode?: string;
}

export interface AttendanceEntry {
  id?: number;
  attendanceDate: string; // yyyy-MM-dd
  personnelUid: string;
  shiftId?: number | null;
  shiftName?: string | null;
  checkInTime?: string | null;
  checkInStatus?: string | null;
  checkInLocation?: string | null;
  checkInDevice?: string | null;
  checkOutTime?: string | null;
  checkOutStatus?: string | null;
  checkOutLocation?: string | null;
  checkOutDevice?: string | null;
  notes?: string | null;
  isManuallyEdited?: boolean;
  workedMinutes?: number | null;
  [key: string]: any;
}

export interface AttendanceListParams {
  from?: string;
  to?: string;
  personnelUid?: string;
  shiftId?: number;
  status?: string;
  page?: number;
  pageSize?: number;
}

export interface AttendanceListResult {
  items: AttendanceEntry[];
  total: number;
  page: number;
  pageSize: number;
  from: string;
  to: string;
}

export interface ToplamSureItem {
  personnelUid: string;
  totalMinutes: number;
  dayCount: number;
}

export interface AttendanceEditInput {
  uid: string;
  date: string;
  shiftId?: number;
  shiftName?: string;
  /** true ise giriş alanları null olsa bile uygulanır (temizleme). */
  setCheckIn?: boolean;
  /** true ise çıkış alanları null olsa bile uygulanır (temizleme). */
  setCheckOut?: boolean;
  checkInTime?: string | null;
  checkInStatus?: string | null;
  checkInLocationJson?: string | null;
  checkOutTime?: string | null;
  checkOutStatus?: string | null;
  checkOutLocationJson?: string | null;
  notes?: string | null;
}

// ===================== MAP YARDIMCILARI =====================

function mapSqlShift(s: any): Shift {
  return {
    ...s,
    id: s?.id,
    name: s?.name || 'İsimsiz Vardiya',
    startTime: s?.startTime || '00:00',
    endTime: s?.endTime || '00:00',
    lateTolerance: s?.lateTolerance ?? 10,
    earlyExitTolerance: s?.earlyExitTolerance ?? 10,
    isNextDayExit: s?.isNextDayExit ?? false,
    branchesId: s?.branchesId ?? null,
    attendanceMode: s?.attendanceMode === 'dual' ? 'dual' : 'single',
    legacyFirebaseKey: s?.legacyFirebaseKey ?? null,
    isActive: s?.isActive ?? true,
    assignedCount: s?.assignedCount ?? 0,
  };
}

// ===================== VARDIYA (SHIFTS) =====================

/** Sirketin vardiyalarini getirir (SADECE SQL). */
export async function getShifts(_companyId: string): Promise<Shift[]> {
  const res = await hrApi.get('/api/hr/shifts');
  const raw = Array.isArray(res.data?.items) ? res.data.items : [];
  return raw.map(mapSqlShift);
}

/** Vardiyaya atanan personel uid listesi (SQL). */
export async function getShiftPersonnel(shiftId: number | string): Promise<string[]> {
  const res = await hrApi.get(`/api/hr/shifts/${shiftId}/personnel`);
  return Array.isArray(res.data?.personnelUids) ? res.data.personnelUids : [];
}

/** Personelin atandigi vardiya (mobil findPersonnelShift muadili). */
export async function getShiftByPersonnel(uid: string): Promise<Shift | null> {
  const res = await hrApi.get(`/api/hr/shifts/by-personnel/${encodeURIComponent(uid)}`);
  if (res.data?.found && res.data?.data) return mapSqlShift(res.data.data);
  return null;
}

/** Yeni vardiya olusturur (SADECE SQL). Donus: yeni vardiya id. */
export async function createShift(input: ShiftWriteInput): Promise<number> {
  const res = await hrApi.post('/api/hr/shifts/create', {
    name: input.name,
    startTime: input.startTime,
    endTime: input.endTime,
    lateTolerance: input.lateTolerance ?? 0,
    earlyExitTolerance: input.earlyExitTolerance ?? 0,
    isNextDayExit: input.isNextDayExit ?? false,
    branchesId: input.branchesId,
    attendanceMode: input.attendanceMode,
  });
  const id = res?.data?.data?.id;
  if (id == null) throw new Error('createShift: sunucu id dondurmedi');
  return id;
}

/** Vardiya gunceller (SADECE SQL). */
export async function updateShift(shiftId: number | string, input: Partial<ShiftWriteInput> & { isActive?: boolean }): Promise<void> {
  await hrApi.post('/api/hr/shifts/update', {
    id: shiftId,
    name: input.name,
    startTime: input.startTime,
    endTime: input.endTime,
    lateTolerance: input.lateTolerance,
    earlyExitTolerance: input.earlyExitTolerance,
    isNextDayExit: input.isNextDayExit,
    branchesId: input.branchesId,
    attendanceMode: input.attendanceMode,
    isActive: input.isActive,
  });
}

/** Vardiya siler (SADECE SQL — soft delete + atamalari temizler). */
export async function deleteShift(shiftId: number | string): Promise<void> {
  await hrApi.post('/api/hr/shifts/delete', { id: shiftId });
}

/** Vardiyaya personel atar (replace; enforceSingleShift varsayilan true). */
export async function assignShiftPersonnel(
  shiftId: number | string,
  personnelUids: string[],
  enforceSingleShift = true
): Promise<void> {
  await hrApi.post(`/api/hr/shifts/${shiftId}/assign`, { personnelUids, enforceSingleShift });
}

// ===================== ATTENDANCE (GIRIS/CIKIS) =====================

function mapAttendance(e: any): AttendanceEntry {
  return { ...e };
}

/** Giriş/çıkış kayitlari (SQL, server-side pagination). */
export async function getAttendanceList(params: AttendanceListParams = {}): Promise<AttendanceListResult> {
  const res = await hrApi.get('/api/hr/attendance/list', { params });
  return {
    items: (Array.isArray(res.data?.items) ? res.data.items : []).map(mapAttendance),
    total: res.data?.total ?? 0,
    page: res.data?.page ?? 1,
    pageSize: res.data?.pageSize ?? 50,
    from: res.data?.from,
    to: res.data?.to,
  };
}

/** Giriş-çıkış raporlari (list kisayolu). */
export async function getGirisCikisRaporlari(params: AttendanceListParams = {}): Promise<AttendanceListResult> {
  const res = await hrApi.get('/api/hr/attendance/giris-cikis-raporlari', { params });
  return {
    items: (Array.isArray(res.data?.items) ? res.data.items : []).map(mapAttendance),
    total: res.data?.total ?? 0,
    page: res.data?.page ?? 1,
    pageSize: res.data?.pageSize ?? 50,
    from: res.data?.from,
    to: res.data?.to,
  };
}

/** Toplam çalisma suresi (personel basina dakika + gun). */
export async function getToplamSure(params: { from?: string; to?: string; personnelUid?: string } = {}): Promise<ToplamSureItem[]> {
  const res = await hrApi.get('/api/hr/attendance/toplam-sure', { params });
  return Array.isArray(res.data?.items) ? res.data.items : [];
}

/** Tek gun/personel kaydi. */
export async function getAttendanceByDateUid(date: string, uid: string): Promise<AttendanceEntry | null> {
  const res = await hrApi.get(`/api/hr/attendance/${encodeURIComponent(date)}/${encodeURIComponent(uid)}`);
  if (res.data?.found && res.data?.data) return mapAttendance(res.data.data);
  return null;
}

/** Manuel giriş/çikis duzenleme (SADECE SQL — IsManuallyEdited=true). */
export async function editAttendance(input: AttendanceEditInput): Promise<AttendanceEntry> {
  const res = await hrApi.post('/api/hr/attendance/edit', input);
  return mapAttendance(res.data?.data);
}

// ===================== DUAL: PUNCH DUZENLEME (Faz C) =====================

export interface PunchEditInput {
  id: number;
  /** TR yerel ISO (UTC 'Z'); null gonderilen alan degistirilmez. */
  time?: string | null;
  /** "in" | "out"; null → degistirme. */
  direction?: string | null;
  status?: string | null;
  notes?: string | null;
}

export interface PunchAddInput {
  uid: string;
  /** "in" | "out". */
  direction: string;
  /** TR yerel ISO (UTC 'Z'). */
  time: string;
  shiftId?: number | null;
  shiftName?: string | null;
  status?: string | null;
  branchId?: string | null;
  notes?: string | null;
}

/** Var olan punch'i duzenle (dual). Backend AttendanceDate'i yeniden hesaplar. */
export async function editPunch(input: PunchEditInput): Promise<void> {
  await hrApi.post('/api/hr/attendance/punch/edit', {
    id: input.id,
    time: input.time,
    direction: input.direction,
    status: input.status,
    notes: input.notes,
  });
}

/** Eksik giriş/çıkış punch'i ekle (anomali tamamlama → tam oturum). Donen yeni id. */
export async function addPunch(input: PunchAddInput): Promise<number | undefined> {
  const res = await hrApi.post('/api/hr/attendance/punch/add', {
    uid: input.uid,
    direction: input.direction,
    time: input.time,
    shiftId: input.shiftId,
    shiftName: input.shiftName,
    status: input.status,
    branchId: input.branchId,
    notes: input.notes,
  });
  return res?.data?.data?.id;
}

/** Hatalı punch'i sil (dual manuel düzeltme). */
export async function deletePunch(id: number): Promise<void> {
  await hrApi.post('/api/hr/attendance/punch/delete', { id });
}

// ===================== OKUTMA KAYIT DEFTERI (ShiftScanEvents) =====================

/** Kayıt defteri olayı. Zamanlar UTC ISO ('Z'). */
export interface ScanEvent {
  id: number;
  personnelUid: string;
  /** scan | client_failure | reminder | manager_edit */
  eventType: string;
  /** recorded | confirm_required | confirmed | corrected | deduped | min_interval | … */
  outcome: string;
  qrDirection?: string | null;
  qrBranchId?: string | null;
  recordedDirection?: string | null;
  punchId?: number | null;
  shiftId?: number | null;
  openInTime?: string | null;
  clientEventId?: string | null;
  clientTime?: string | null;
  eventTime: string;
  messageShown?: string | null;
  appVersion?: string | null;
  platform?: string | null;
  deviceModel?: string | null;
  durationMs?: number | null;
  actorName?: string | null;
  oldValue?: string | null;
  newValue?: string | null;
  detailJson?: string | null;
}

export interface ScanEventTotals {
  scans: number;
  recorded: number;
  warnings: number;
  confirmed: number;
  corrected: number;
  abandoned: number;
  deduped: number;
  rejected: number;
  failures: number;
  failuresActuallyRecorded: number;
  managerEdits: number;
  reminders: number;
  missingOut: number;
  orphanOut: number;
  completeSessions: number;
}

export interface ScanEventPerson extends ScanEventTotals {
  personnelUid: string;
  manualPunches: number;
  appVersion?: string | null;
  lastEventTime?: string | null;
}

export interface ScanEventSummary {
  /** true → bu şirketin veritabanında kayıt defteri tablosu yok (SQL çalıştırılmamış). */
  logDisabled?: boolean;
  from: string;
  to: string;
  totals: ScanEventTotals;
  people: ScanEventPerson[];
}

/** Özet: üst sayaçlar + personel bazında davranış (tarih: yyyy-MM-dd, TR günü). */
export async function getScanEventSummary(params: { from?: string; to?: string } = {}): Promise<ScanEventSummary> {
  const res = await hrApi.get('/api/hr/attendance/scan-events/summary', { params });
  const d = res?.data ?? {};
  return { logDisabled: !!d.logDisabled, from: d.from, to: d.to, totals: d.totals, people: d.people ?? [] };
}

/** Olay listesi (personel / tür / sonuç filtresi). */
export async function getScanEvents(params: {
  from?: string;
  to?: string;
  personnelUid?: string;
  eventType?: string;
  outcome?: string;
  page?: number;
  pageSize?: number;
} = {}): Promise<{ items: ScanEvent[]; total: number }> {
  const res = await hrApi.get('/api/hr/attendance/scan-events', { params });
  const d = res?.data ?? {};
  return { items: d.items ?? [], total: d.total ?? 0 };
}

/** SqlOnly mod yardimcisi (cagiranlar fallback davranisini ayarlayabilir). */
export function isSqlOnly(): boolean {
  return getMigrationMode() === 'SqlOnly';
}
